/**
 * Inline fixups for the Konfidency reviews loader, ported from the Fresh origin's
 * `components/GlobalTags.tsx`. Both run as plain <head> scripts (see
 * `src/routes/__root.tsx`) and are defensive by design: the loader itself is
 * `defer`, so neither can assume `window.konfidencyLoader` exists yet.
 */

/**
 * Dedupe do `GET /<conta>/top-rated/active`.
 *
 * O selo Top Rated é um componente Vue cujo `beforeMount` faz
 * `axios.get(".../top-rated/active")` — **um request por instância**. Uma PLP de
 * 36 cards dispara 36 requests idênticos no mesmo tick, mesma resposta de 1,9 KB.
 * Não há memo nem cache no SDK (conferido no chunk `9113.369a23da.js`).
 *
 * A resposta tem `cache-control: max-age=3600`, o que engana em profile quente.
 * Não cobre o cold path: os 36 saem antes de o primeiro voltar e **o Chrome não
 * coalesce GETs cross-origin concorrentes** — medido com URL fora do cache, os
 * 36 levam >50 ms cada (até 190 ms).
 *
 * **Cuidado ao medir**: `transferSize` é 0 em TODA resposta cross-origin sem
 * `Timing-Allow-Origin`, então não serve de prova de cache hit. Use `duration`.
 *
 * Fix: o primeiro XHR sai na rede; os demais são atendidos com a resposta dele,
 * sem tocar a rede. Axios lê `status`/`responseText`/`getAllResponseHeaders()`
 * do objeto XHR, então basta sombrear esses getters na instância e disparar
 * `readystatechange`/`load`/`loadend`. Os que chegam enquanto o primeiro ainda
 * está no ar entram numa fila; se ele falhar (status fora de 2xx, timeout de
 * 8 s), a fila cai no `send` real e o comportamento é o de antes.
 *
 * Medido em /beleza-e-higiene: **36 requests → 1**, com os mesmos 3 selos e as
 * mesmas 432 estrelas renderizadas.
 *
 * Prefetch no gate não resolveria: só existem ~37 ms entre injetar o loader e a
 * rajada, e o request cold leva ~190 ms.
 *
 * Bug do SDK, não da migração: a origem em Fresh roda o mesmo loader.
 */
export const KONFIDENCY_TOP_RATED_DEDUPE = `(() => {
  var HOST = "reviews-api.konfidency.com.br";
  var PATH = "/top-rated/active";
  var P = XMLHttpRequest.prototype;
  var open = P.open;
  var send = P.send;
  var cache = {};
  var waiters = {};

  var define = function (x, key, value) {
    Object.defineProperty(x, key, { configurable: true, get: function () { return value; } });
  };

  var replay = function (x, entry) {
    define(x, "readyState", 4);
    define(x, "status", entry.status);
    define(x, "statusText", entry.statusText);
    define(x, "responseURL", entry.responseURL);
    define(x, "responseText", entry.text);
    define(x, "response", x.responseType === "json" ? JSON.parse(entry.text) : entry.text);
    x.getAllResponseHeaders = function () { return entry.headers; };
    x.getResponseHeader = function (name) {
      var re = new RegExp("^" + String(name).replace(/[.*+?^\${}()|[\\]\\\\]/g, "\\\\$&") + ":\\\\s*(.*)$", "im");
      var m = entry.headers.match(re);
      return m ? m[1].trim() : null;
    };
    setTimeout(function () {
      try {
        x.dispatchEvent(new Event("readystatechange"));
        x.dispatchEvent(new ProgressEvent("load"));
        x.dispatchEvent(new ProgressEvent("loadend"));
      } catch (_) {}
    }, 0);
  };

  P.open = function (method, url) {
    var u = String(url);
    var isGet = !method || String(method).toUpperCase() === "GET";
    this.__kfyUrl = isGet && u.indexOf(HOST) !== -1 && u.indexOf(PATH) !== -1 ? u : null;
    return open.apply(this, arguments);
  };

  P.send = function () {
    var self = this;
    var args = arguments;
    var u = self.__kfyUrl;
    if (!u) return send.apply(self, args);

    var real = function () { return send.apply(self, args); };

    if (cache[u]) return replay(self, cache[u]);

    if (!waiters[u]) {
      waiters[u] = [];
      self.addEventListener("loadend", function () {
        if (self.readyState === 4 && self.status >= 200 && self.status < 300) {
          cache[u] = {
            status: self.status,
            statusText: self.statusText,
            responseURL: self.responseURL,
            text: self.responseText,
            headers: self.getAllResponseHeaders(),
          };
        }
        var queued = waiters[u];
        waiters[u] = null;
        queued.forEach(function (fn) { try { fn(); } catch (_) {} });
      });
      setTimeout(function () {
        if (!waiters[u]) return;
        var queued = waiters[u];
        waiters[u] = null;
        queued.forEach(function (fn) { try { fn(); } catch (_) {} });
      }, 8000);
      return real();
    }

    waiters[u].push(function () { cache[u] ? replay(self, cache[u]) : real(); });
  };
})();`;

/**
 * O SDK só chama attachTopRatedBadge uma vez no boot.
 * Prateleiras Deco entram depois (lazy) — reanexa como o loadShowcase faz com as estrelas.
 */
export const KONFIDENCY_TOP_RATED_RETRY = `(() => {
  const delays = [500, 1000, 2000, 5000, 10000];
  const attach = () => {
    try {
      if (window.konfidencyLoader && window.konfidencyLoader.attachTopRatedBadge) {
        window.konfidencyLoader.attachTopRatedBadge();
      }
    } catch (_) {}
  };
  delays.forEach((ms) => setTimeout(attach, ms));
  let scheduled;
  const observer = new MutationObserver(() => {
    if (scheduled) return;
    scheduled = setTimeout(() => {
      scheduled = undefined;
      attach();
    }, 300);
  });
  const start = () =>
    observer.observe(document.documentElement, { childList: true, subtree: true });
  if (document.readyState === "loading") {
    document.addEventListener("DOMContentLoaded", start);
  } else {
    start();
  }
})();`;

/**
 * SDK lê settings.carouselBreakpoints no beforeMount do carrossel.
 * Default da conta é 5 no desktop — forçamos 4 e remontamos se já tiver montado.
 */
export const KONFIDENCY_CAROUSEL_FOUR_SLIDES = `(() => {
  const breakpoints = {
    320: { slidesPerView: 1 },
    640: { slidesPerView: 2 },
    900: { slidesPerView: 3 },
    1100: { slidesPerView: 4 },
  };

  const patch = () => {
    const customer = window.konfidencyCustomer;
    if (!customer || !customer.settings) return false;
    customer.settings.carouselBreakpoints = breakpoints;
    return true;
  };

  let remounted = false;
  const remountIfNeeded = () => {
    if (remounted || !patch()) return;
    const roots = document.querySelectorAll(".konfidency-storeReviewCarousel");
    if (!roots.length) return;
    let hadCarousel = false;
    roots.forEach((root) => {
      if (!root.querySelector("#konfidency-storeReview-carousel")) return;
      hadCarousel = true;
      const next = root.cloneNode(false);
      root.parentNode && root.parentNode.replaceChild(next, root);
    });
    if (!hadCarousel) return;
    remounted = true;
    try {
      window.konfidencyLoader &&
        window.konfidencyLoader.attachStoreReviewsCarousel &&
        window.konfidencyLoader.attachStoreReviewsCarousel();
    } catch (_) {}
  };

  const early = setInterval(() => {
    patch();
  }, 30);

  [600, 1200, 2500, 5000, 8000].forEach((ms) => setTimeout(remountIfNeeded, ms));
  setTimeout(() => clearInterval(early), 12000);
})();`;

/**
 * Estrelas e "Clique e veja!" abrem o modal.
 * goToDetails só abre o modal quando #konfidency-reviews-container não existe;
 * se a seção já carregou, ele rola a página. Tiramos o id, disparamos o clique
 * da Konfidency e devolvemos o id na hora — antes de qualquer microtask.
 * (miess-01 #c30258)
 */
export const KONFIDENCY_STARS_OPEN_MODAL = `(() => {
  const containerId = "konfidency-reviews-container";
  let opening = false;

  const anchorInRow = (link) => {
    const row = link && link.parentElement;
    return row ? row.querySelector(".konfidency-reviews-summary .reviews-anchor") : null;
  };

  document.addEventListener("click", (event) => {
    if (opening) return;
    const target = event.target;
    if (!target || !target.closest) return;

    const star = target.closest(".konfidency-reviews-summary .reviews-anchor");
    const link = target.closest('a[href="#konfidency-reviews"]');
    const anchor = star || anchorInRow(link);
    if (!anchor) return;

    event.preventDefault();
    event.stopPropagation();

    const container = document.getElementById(containerId);
    if (container) container.removeAttribute("id");
    opening = true;
    try {
      anchor.dispatchEvent(new MouseEvent("click", { bubbles: true, cancelable: true }));
    } finally {
      opening = false;
      if (container) container.id = containerId;
    }
  }, true);
})();`;
